/**
 * Where the website's "download the app" buttons send a visitor.
 *
 * The marketing site is only ever seen in a BROWSER — inside the native app
 * `/` redirects straight to the dashboard (see NativeStartRedirect in
 * App.tsx) — so the device has to be read off the user agent rather than
 * Capacitor.getPlatform(), which reports "web" for mobile Safari and Chrome
 * alike. A member who already has the app installed therefore never sees
 * these buttons.
 *
 * Both listings are live. hasStoreUrl() still gates every caller, so if one
 * of these URLs is ever blanked out, that platform falls back to the web app
 * rather than handing a button an empty href (see appDownloadUrl below).
 */

/**
 * The two ids every store link is built from, so a download link and a
 * "rate us" link can never drift apart (reviewConfig.ts builds its own
 * destinations from these).
 *
 * APPLE_APP_ID is the numeric App Store ID Apple assigns on first
 * submission (App Store Connect → App Information) — NOT the bundle id,
 * and not derivable from this repo, hence the literal.
 */
export const APPLE_APP_ID = "6777436677";

/** Play addresses a listing by package name; this is capacitor.config.ts's. */
export const ANDROID_PACKAGE = "com.zuzu.wellness";

/**
 * App Store listing.
 *
 * Note the listing is named "zuzu recovery club" while capacitor.config.ts
 * still carries appName "ZUZU Wellness" and the bundle id com.zuzu.wellness.
 * That is a naming drift, not a broken link: Apple resolves the listing by
 * its id and the slug in the path is decorative.
 */
export const APP_STORE_URL = `https://apps.apple.com/za/app/zuzu-recovery-club/id${APPLE_APP_ID}`;

/** Google Play listing. */
export const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;

export type MobileOS = "ios" | "android" | null;

/**
 * Which phone OS this browser is running on, or null on desktop.
 *
 * iPadOS 13+ reports itself as a Mac in the user agent, so an iPad is
 * identified by the one thing that still separates it from a real Mac: a
 * touch screen.
 */
export function detectMobileOS(ua?: string, maxTouchPoints?: number): MobileOS {
  const agent =
    ua ?? (typeof navigator === "undefined" ? "" : navigator.userAgent);
  const touch =
    maxTouchPoints ??
    (typeof navigator === "undefined" ? 0 : navigator.maxTouchPoints || 0);
  if (!agent) return null;
  if (/android/i.test(agent)) return "android";
  if (/iPad|iPhone|iPod/i.test(agent)) return "ios";
  if (/Macintosh/i.test(agent) && touch > 1) return "ios";
  return null;
}

/** The store link for a phone OS, or "" when there isn't a usable one. */
export function storeUrlFor(os: MobileOS): string {
  if (os === "ios") return APP_STORE_URL.trim();
  if (os === "android") return PLAY_STORE_URL.trim();
  return "";
}

/** True once a platform has a real (http) store destination configured. */
export function hasStoreUrl(os: MobileOS): boolean {
  return /^https?:\/\//.test(storeUrlFor(os));
}

/**
 * Where a "download the app" button should point right now.
 *
 * On a phone with a published listing that's the store. Everywhere else —
 * desktop, or a platform whose listing isn't configured yet — it's the web
 * app, which is a working destination rather than a dead end.
 */
export function appDownloadUrl(
  webAppPath: string,
  os: MobileOS = detectMobileOS(),
): string {
  return hasStoreUrl(os) ? storeUrlFor(os) : webAppPath;
}

/** True when the button will leave the site for an app store. */
export function goesToStore(os: MobileOS = detectMobileOS()): boolean {
  return hasStoreUrl(os);
}
